import React, { createContext, useCallback, useEffect, useState } from 'react';
import { Global, css } from '@emotion/react';

/**
 * The new portal's look: direction 3b of the 27 Oct redesign
 * (docs: redesign-27oct/coach/TOKENS.md and REDESIGN-KIT.md). Ink on paper:
 * warm charcoal or warm off-white, cards with 1px hairlines, no shadows except
 * floating layers, no gradients except the --stripe placeholder, and no accent
 * colour unless the coach picks one (Settings → Appearance; see ./appearance).
 * The only colour of its own is red, for urgent and destructive things.
 *
 * Every colour is a CSS variable on the portal's root (`.sx`), swapped by
 * data-theme, so nothing here touches the current portal's styles.
 *
 * Roles (kit §2):
 *   --bg canvas · --side sidebar · --card cards, menus, dialogs ·
 *   --row row hover, tracks, skeletons, segmented track, neutral avatar fill ·
 *   --line hairlines · --line2 control borders ·
 *   --tx / --tx2 / --tx3 ink · --err urgent and destructive (--err-tx as text, --err-soft tint) ·
 *   --acc / --acc-ink / --acc-soft the accent (= ink unless the coach sets one).
 *
 * The accent may colour ONLY: the active nav bar (3px) and active tab
 * underline, progress fills, the primary button fill, the Ask sparkle and
 * send button, and check-in day squares (22% alpha). The 4 Nov package (§D)
 * adds: the open menu item's icon, Home's date eyebrow and top-right glow,
 * Getting started's ring and button, and the tour's ring, card and dots.
 * Nothing else.
 */
const DARK = `
  --bg:#141311;--side:#171615;--card:#1C1B19;--row:#232220;
  --line:#2A2826;--line2:#3A3733;
  --tx:#F3F1EC;--tx2:#B5B1AA;--tx3:#8E8A84;
  --err:#D23A63;--err-tx:#E85C82;--err-soft:rgba(210,58,99,.14);
  --acc:var(--tx);--acc-ink:var(--bg);--acc-soft:rgba(243,241,236,.10);
  --stripe:repeating-linear-gradient(135deg,#2A2826 0 12px,#34312D 12px 24px);
  --shadow-float:0 20px 50px rgba(0,0,0,.25);
  --scrim:rgba(0,0,0,.55);--ring:rgba(243,241,236,.22);
  color-scheme: dark;
`;

const LIGHT = `
  --bg:#EFEDE8;--side:#F7F5F1;--card:#FFFFFF;--row:#F4F2ED;
  --line:#E7E3DB;--line2:#D4CFC5;
  --tx:#171615;--tx2:#57534D;--tx3:#7D786F;
  --err:#D23A63;--err-tx:#B42318;--err-soft:rgba(210,58,99,.10);
  --acc:var(--tx);--acc-ink:var(--bg);--acc-soft:rgba(23,22,21,.08);
  --stripe:repeating-linear-gradient(135deg,#E6E2DA 0 12px,#DCD7CD 12px 24px);
  --shadow-float:0 20px 50px rgba(20,20,20,.12);
  --scrim:rgba(20,20,20,.30);--ring:rgba(23,22,21,.18);
  color-scheme: light;
`;

/** Each theme's tokens as CSS text, for a block that shows the other theme (Settings → Appearance's preview). */
export const THEME_VARS: Record<'dark' | 'light', string> = { dark: DARK, light: LIGHT };

/** The same in both themes: radii (cards 12, buttons and inputs 8, chips 6, nav items 8). */
const SHARED = `
  --r-card:12px;--r-btn:8px;--r-chip:6px;--r-nav:8px;
`;

/**
 * The v2 names, kept working until every page is restyled (page builders:
 * replace each use with the new token and delete the alias when nothing uses
 * it; `grep -rn "var(--s1)" src/new`). They are mapped by ROLE, not by colour,
 * so a page that still uses them already reads as 3b:
 *
 * | old            | now                 | why |
 * |----------------|---------------------|-----|
 * | --s1           | --card              | cards, panels, menus |
 * | --s2           | --row               | hovers, soft fills, secondary fills |
 * | --s3           | --line              | pressed, small neutral fills (was #2A2826 = --line) |
 * | --panel        | --bg                | no floating main panel in 3b |
 * | --panel-bd     | --line              | |
 * | --row-hi       | --row               | hover (old cards were --row; they are --card now) |
 * | --nav-on       | --row               | the chosen nav item |
 * | --pri/--pri-ink| --acc/--acc-ink     | the primary button |
 * | --y/--y-ink    | --tx/--bg           | counts and badges are ink, never yellow |
 * | --y-soft       | --row               | |
 * | --link/--link-u| --tx/none           | links are ink |
 * | --ok           | --tx2               | success is neutral (kit §3: status chips are --tx2 outlines) |
 * | --info         | --tx2               | info is neutral |
 * | --warn/--warn-tx | --tx2             | no amber; urgent things use --err |
 * | --ok-soft, --info-soft, --warn-soft | --row | neutral tint |
 * | --shadow       | --shadow-float      | only floating layers have a shadow |
 */
const ALIASES = `
  --s1:var(--card);--s2:var(--row);--s3:var(--line);
  --panel:var(--bg);--panel-bd:var(--line);--row-hi:var(--row);--nav-on:var(--row);
  --pri:var(--acc);--pri-ink:var(--acc-ink);
  --y:var(--tx);--y-ink:var(--bg);--y-soft:var(--row);
  --link:var(--tx);--link-u:none;
  --ok:var(--tx2);--ok-soft:var(--row);
  --info:var(--tx2);--info-soft:var(--row);
  --warn:var(--tx2);--warn-tx:var(--tx2);--warn-soft:var(--row);
  --shadow:var(--shadow-float);
`;

export const FONT = "'Hanken Grotesk', system-ui, sans-serif";
export const MONO = "'IBM Plex Mono', ui-monospace, monospace";
export const ICON = "'Material Symbols Rounded'";

const FONT_LINKS = [
  'https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600&display=swap',
  'https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..24,400,0..1,0&display=block',
];

/** Connections opened before the stylesheets ask for them (the font files come from gstatic). */
const PRECONNECT = [
  { href: 'https://fonts.googleapis.com', crossOrigin: false },
  { href: 'https://fonts.gstatic.com', crossOrigin: true },
];

/** Loads the new portal's fonts once, the first time it opens. */
export const useFonts = () => {
  useEffect(() => {
    PRECONNECT.forEach(({ href, crossOrigin }) => {
      if (!document.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        if (crossOrigin) link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      }
    });
    FONT_LINKS.forEach((href) => {
      if (!document.querySelector(`link[href="${href}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        document.head.appendChild(link);
      }
    });
  }, []);
};

export type TTheme = 'dark' | 'light';
const THEME_KEY = 'sx-theme';

const readTheme = (): TTheme => {
  try {
    return localStorage.getItem(THEME_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
};

/** Dark or light, remembered on this device. */
export const useTheme = () => {
  const [theme, setTheme] = useState<TTheme>(readTheme);
  const toggle = useCallback(() => {
    setTheme((now) => {
      const next = now === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch {
        // A blocked store only means the choice isn't remembered.
      }
      return next;
    });
  }, []);
  return { theme, toggle };
};

/**
 * The portal's theme and its switch, for a panel with a theme button of its own (the exercise
 * thread panel, 29 Oct). Null outside the portal (tests): the button isn't shown.
 */
export const ThemeToggleContext = createContext<{ theme: TTheme; toggle: () => void } | null>(null);

/**
 * The one entrance animation (kit §2 Motion): a short fade-up, 0.45–0.7s,
 * staggered at most 80ms per block. Add `className="sx-rise"` (and an
 * `animationDelay`) to a block; it does nothing under reduced motion.
 */
export const RISE_CLASS = 'sx-rise';

/** Tokens and base styles, scoped to the new portal's root (`.sx`). */
export const PortalStyles = () => (
  <Global
    styles={css`
      .sx { ${DARK} ${SHARED} ${ALIASES} }
      .sx[data-theme='light'] { ${LIGHT} }
      .sx {
        height: 100vh;
        background: var(--bg);
        color: var(--tx);
        font-family: ${FONT};
        font-size: 14px;
        line-height: 1.4;
        -webkit-font-smoothing: antialiased;
      }
      .sx *, .sx *::before, .sx *::after { box-sizing: border-box; }
      /*
       * The current portal's index.css sets Jost on every element (html body *), which beats
       * inheritance. This takes it back for the new portal's elements with just enough weight
       * (0,0,3) to win over that rule and lose to any component's own font (a class).
       */
      html body div:where(.sx) * { font-family: ${FONT}; }
      /* Defaults only: :where() adds no weight, so any component's own styles win. */
      :where(.sx) a { color: var(--tx); text-decoration: none; }
      :where(.sx) :where(button, input, select, textarea) { font: inherit; color: inherit; }
      :where(.sx) :where(h1, h2, h3, p) { margin: 0; }
      .sx :focus-visible { outline: 2px solid var(--ring); outline-offset: 2px; }
      .sx .mono { font-family: ${MONO}; }
      /* Mono caps label (kit: 11px, +0.12em, --tx3). */
      .sx .caps { font-family: ${MONO}; font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--tx3); font-weight: 500; }
      @keyframes sxRise { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
      .sx .${RISE_CLASS} { animation: sxRise 0.55s ease both; }
      @keyframes sxShimmer { 0% { opacity: .45 } 50% { opacity: .9 } 100% { opacity: .45 } }
      @keyframes sxSpin { to { transform: rotate(360deg) } }
      @media (prefers-reduced-motion: reduce) {
        .sx .${RISE_CLASS} { animation: none !important; }
        .sx *, .sx *::before, .sx *::after { animation-duration: 1ms !important; transition-duration: 1ms !important; }
      }
    `}
  />
);
